// Settings → AI models: the tenant's own OpenRouter connection and per-task model picks.
// Contract: packages/shared/src/features/ai.ts (GET/PUT /api/ai, /api/ai/models, /api/ai/openrouter/*).

import type { AiModelsRes, AiSettingsPut, AiSettingsRes, AiTask, AiTestRes } from "@ml/shared/features/ai";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";

export const AI_KEYS = {
  settings: ["ai", "settings"] as const,
  models: ["ai", "models"] as const,
};

/** Where OpenRouter sends the browser back after sign-in (must match the API's OAUTH_CALLBACK_PATH). */
export const OPENROUTER_CALLBACK_PATH = "/settings/ai/openrouter";

export const aiApi = {
  settings: () => api<AiSettingsRes>("/api/ai"),
  models: () => api<AiModelsRes>("/api/ai/models"),
  save: (body: AiSettingsPut) => api<AiSettingsRes>("/api/ai", { method: "PUT", body }),
  startOAuth: () =>
    api<{ url: string }>("/api/ai/openrouter/start", {
      method: "POST",
      body: { callbackUrl: `${window.location.origin}${OPENROUTER_CALLBACK_PATH}` },
    }),
  finishOAuth: (code: string, state: string) => api<AiSettingsRes>("/api/ai/openrouter/callback", { method: "POST", body: { code, state } }),
  saveKey: (key: string) => api<AiSettingsRes>("/api/ai/openrouter/key", { method: "PUT", body: { key } }),
  disconnect: () => api<AiSettingsRes>("/api/ai/openrouter", { method: "DELETE" }),
  test: (task: AiTask) => api<AiTestRes>("/api/ai/test", { method: "POST", body: { task } }),
};

export function useAiSettings() {
  return useQuery({ queryKey: AI_KEYS.settings, queryFn: aiApi.settings });
}

/** The OpenRouter catalog changes slowly; the API caches it for 6h too. */
export function useAiModels(enabled = true) {
  return useQuery({ queryKey: AI_KEYS.models, queryFn: aiApi.models, staleTime: 30 * 60_000, enabled });
}

/** Any call that returns fresh AiSettingsRes writes it straight into the cache. */
export function useAiMutation<V>(fn: (v: V) => Promise<AiSettingsRes>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (data) => qc.setQueryData(AI_KEYS.settings, data),
  });
}
